import { Controller } from "@hotwired/stimulus"

// Drives the Stripe Payment Element on the public /pay/:token page (Phase 6).
//
// It mounts a Payment Element, and on submit:
//   1. confirms the PaymentMethod client-side (createPaymentMethod) so we learn
//      the card funding type (credit vs debit) WITHOUT charging,
//   2. posts payment_method_id + funding to the server, which builds the DIRECT
//      charge on the connected account (surcharge gated on funding === "credit").
//
// `window.Stripe` is the global injected by Stripe.js. In SYSTEM TESTS this is
// STUBBED with a test double (test/support/stripe_js_stub) so there is NO network
// call and NO console error, and a "pay" can be simulated to success/failure.
export default class extends Controller {
  static values = {
    publishableKey: String,
    account: String,
    clientSecretEndpoint: String,
    // "Run a card" (operator MOTO / keyed entry) → a plain Card Element: no Link,
    // no wallets, just the card fields the tenant reads over the phone. The public
    // /pay page leaves this false so tenants keep Link + wallets for fast checkout.
    cardOnly: Boolean,
  }
  static targets = ["element", "submit", "error", "fundingField", "form"]

  connect() {
    if (typeof window.Stripe !== "function") {
      // Stripe.js not present (offline / blocked). Leave the manual fallback UI;
      // do not throw — a thrown error would fail the console-error assertion.
      this.showError("Payment is temporarily unavailable. Please try again later.")
      return
    }

    this.stripe = window.Stripe(this.publishableKeyValue, this.accountValue ? { stripeAccount: this.accountValue } : undefined)
    if (this.cardOnlyValue) {
      // Plain Card Element — keyed card entry only (no Link / no wallets).
      this.elements = this.stripe.elements()
      this.paymentElement = this.elements.create("card")
    } else {
      this.elements = this.stripe.elements({ mode: "setup", currency: "usd", paymentMethodCreation: "manual" })
      this.paymentElement = this.elements.create("payment")
    }
    this.paymentElement.mount(this.elementTarget)
  }

  async submit(event) {
    event.preventDefault()
    if (!this.stripe) return
    this.setBusy(true)
    this.clearError()

    try {
      let result
      if (this.cardOnlyValue) {
        // Card Element: no elements.submit() step — create the PaymentMethod straight
        // from the card field.
        result = await this.stripe.createPaymentMethod({ type: "card", card: this.paymentElement })
      } else {
        const { error: submitError } = await this.elements.submit()
        if (submitError) {
          this.showError(submitError.message)
          this.setBusy(false)
          return
        }
        result = await this.stripe.createPaymentMethod({ elements: this.elements })
      }
      if (result.error) {
        this.showError(result.error.message)
        this.setBusy(false)
        return
      }

      const pm = result.paymentMethod
      const funding = (pm.card && pm.card.funding) || (pm.us_bank_account ? "ach" : "unknown")
      this.fundingFieldTarget.value = funding
      this.element("payment_method_id_field").value = pm.id
      this.formTarget.requestSubmit()
    } catch (e) {
      // REPORT BEFORE APOLOGISING. This catch-all used to swallow the error
      // entirely: it showed the payer a generic message and told nobody. That hid a
      // total failure of the storefront bill-pay page for 25 days (2026-08-01) — the
      // page threw here on every attempt, after Stripe had already accepted the
      // card, and the only channel the bug had was a tenant phoning the operator.
      this.report(e, "submit")
      this.showError("Something went wrong. Please try again.")
      this.setBusy(false)
    }
  }

  // Fire-and-forget beacon to PaymentClientErrorsController. Never awaited, always
  // caught: a payer mid-failure must not also be made to wait on our telemetry, and
  // a reporting outage must never become a payment outage. `keepalive` so it still
  // goes if they close the tab in frustration — which is exactly what the payer we
  // never heard from would have done.
  report(error, stage) {
    try {
      // NOT this.element — this controller defines its own element(id) helper below,
      // which SHADOWS Stimulus's built-in element property. `this.element.dataset`
      // silently reads off a function and throws, which the catch here would then
      // swallow, leaving the beacon permanently silent. Caught by a browser test
      // that showed report() firing nothing while an identical inline fetch worked.
      const body = new URLSearchParams({
        page: this.formTarget.dataset.paymentPage || "unknown",
        path: window.location.pathname,
        stage: stage || "",
        message: String((error && error.message) || error || "unknown error").slice(0, 300),
      })
      fetch("/payment-errors", { method: "POST", body, keepalive: true }).catch(() => {})
    } catch (_) {
      // Telemetry is never worth a second exception on the payment path.
    }
  }

  element(id) {
    return this.formTarget.querySelector(`[data-pay-id="${id}"]`)
  }

  setBusy(busy) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = busy
      this.submitTarget.dataset.busy = busy ? "true" : "false"
    }
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.textContent = ""
  }

  showError(message) {
    if (this.hasErrorTarget) this.errorTarget.textContent = message
  }
}
